import { rufe } from './client';

export const ANLAESSE = ['angebot', 'auftrag', 'rechnung', 'support', 'benachrichtigung'] as const;
export const KUNDENARTEN = ['privat', 'geschaeft', 'oeffentlich'] as const;

export type Anlass = (typeof ANLAESSE)[number];
export type Kundenart = (typeof KUNDENARTEN)[number];

/**
 * Ein Postfach des Hauses (SUP-19).
 *
 * `kennung` und `zugangsschluessel` sind **nicht änderbar**: Aus dem Schlüssel
 * bildet der Adapter die Namen seiner Umgebungsvariablen. Wer ihn ändern
 * könnte, könnte ein Postfach von seinen Zugangsdaten trennen — und es fiele
 * erst auf, wenn nichts mehr hinausgeht.
 */
export type Postfach = {
  readonly id: string;
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly absenderAdresse: string;
  readonly absenderName: string;
  readonly antwortAn: string | null;
  readonly signatur: string | null;
  readonly zugangsart: 'smtp' | 'graph';
  readonly zugangsschluessel: string;
  readonly rueckspiegelung: boolean;
  readonly aktiv: boolean;
  readonly bemerkung: string | null;
  /** Ob Zugangsdaten hinterlegt sind — der Betrieb, nicht die Freigabe. */
  readonly versandbereit: boolean;
  /** Wie die Umgebungsvariablen heißen. Ohne Werte — die stehen nie hier. */
  readonly erwarteteVariablen: readonly string[];
  /** CRM-183: der Weg, über den das Postfach tatsächlich läuft — `demo` stellt nie zu. */
  readonly anbindung: 'smtp' | 'graph' | 'demo';
  /** CRM-183: warum das Postfach nicht angebunden ist (Demo außerhalb einer Demo-Instanz). */
  readonly hinweis: string | null;
} & Bearbeitungsvorgaben;

/**
 * SUP-25, SUP-27 (Auftrag CRM-178): Ziele, Eskalation und
 * Eingangsbestätigung je Postfach. null heißt: kein Ziel, keine Eskalation,
 * Vorgabetext.
 */
export type Bearbeitungsvorgaben = {
  /** SUP-25: Stunden bis zur ersten Antwort, 1 bis 720. */
  readonly reaktionszielStunden: number | null;
  /** SUP-25: Stunden bis zur Lösung, 1 bis 2160, nicht kürzer als das Reaktionsziel. */
  readonly loesungszielStunden: number | null;
  /** SUP-25: Kennung der Rolle, die ein überschrittenes Ziel erfährt. */
  readonly eskalationRolle: string | null;
  /** SUP-27: automatische Eingangsbestätigung mit Vorgangsnummer; Vorgabe aus. */
  readonly eingangsbestaetigung: boolean;
  /** SUP-27: eigener Text; null heißt Vorgabetext. */
  readonly eingangsbestaetigungText: string | null;
};

export type Postfachregel = {
  readonly anlass: string;
  readonly kundenart: string | null;
  readonly postfach: string;
  readonly kennung: string;
  readonly bemerkung: string | null;
};

export type Postfachaenderung = {
  readonly bezeichnung: string;
  readonly absenderAdresse: string;
  readonly absenderName: string;
  readonly antwortAn: string | null;
  readonly signatur: string | null;
  readonly zugangsart: 'smtp' | 'graph';
  readonly rueckspiegelung: boolean;
  readonly aktiv: boolean;
  readonly bemerkung: string | null;
} & Bearbeitungsvorgaben;

export async function postfaecher(): Promise<readonly Postfach[]> {
  return rufe('/postfaecher');
}

export async function regeln(): Promise<readonly Postfachregel[]> {
  return rufe('/postfaecher/regeln');
}

export async function aendern(id: string, eingabe: Postfachaenderung): Promise<Postfach> {
  return rufe(`/postfaecher/${id}`, { verfahren: 'PUT', rumpf: eingabe });
}

export async function regelSetzen(eingabe: {
  readonly anlass: Anlass;
  readonly kundenart: Kundenart | null;
  readonly postfach: string;
}): Promise<readonly Postfachregel[]> {
  return rufe('/postfaecher/regeln/setzen', { verfahren: 'PUT', rumpf: eingabe });
}

export async function regelEntfernen(
  anlass: Anlass,
  kundenart: Kundenart,
): Promise<readonly Postfachregel[]> {
  return rufe(`/postfaecher/regeln?anlass=${anlass}&kundenart=${kundenart}`, {
    verfahren: 'DELETE',
  });
}
